import React, { useEffect, useState } from 'react';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  ContentTemplate,
  ErrorInfo,
  InputLabel,
  SectionHeader,
  SingleSelectionRadio
} from 'web-library';
import dayjs from 'dayjs';
import { useRegistrationReport } from '../../logic/useRegistrationReport';
import { candidateService, KeyValueResponse } from '../../../../api';
import { SubjectExamSchema } from '../../../../api/candidate/registration-report/model';
import RegistrationSelectDate from './RegistrationSelectDate';
import RegistrationExamLevel from './RegistrationExamLevel';
import RegistrationExamSeries from './RegistrationExamSeries';
import DownloadAuditProgressModal from '../../../../components/modals/DownloadAuditProgressModal';

const useStyles = makeStyles()((theme) =>
({
  warningText: {
    width: '100%',
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  formElementContainer: {
    marginBottom: theme.spacing(4),
  },
  dateContainer: {
    width: '30%',
    marginRight: '5%',
    display: 'inline-block',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
    '&:last-child': {
      [theme.breakpoints.down('md')]: {
        marginTop: theme.spacing(2),
      },
    },
  },
  errorContainer: {
    display: 'flex',
  },
  errMsg: {
    marginTop: '0 !important',
  },
  label: {
    color: theme.palette.text.secondary,
  },
  submitButtonContainer: {
    width: '100%',
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: '120px',
  },
  menuItemsContainer: {
    width: '65%',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  chipsContainer: {
    overflowX: 'hidden',
    width: '100%',
  },
  iconButton: {
    position: 'absolute',
    right: '45px',
    top: 'calc(50% - 12px)',
    padding: 0,
  },
  chipLabel: {
    padding: 0,
  },
  inputLabel: {
    marginBottom: theme.spacing(1),
    fontWeight: 700,
    position: 'relative',
    transform: 'none!important',
  },
  rootContainer: {
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.spacing(6),
    padding: '0 !important',
  },
  fromControlContainer: {
    display: 'flex',
    marginBottom: theme.spacing(3),
  },
  radioContainer: {},
}));

const EXAM_LEVEL_PLACEHOLDER = 'Select Exam Level';

const RegistrationReportDetail = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [subjectExamValue, setSubjectExamValue] = useState<SubjectExamSchema[] | []>([]);
  const [examSeriesValue, setExamSeriesValue] = useState<KeyValueResponse | null>(null);
  const [isDisable, setIsDisable] = useState<boolean>(true);
  const [searchTouched, setSearchTouched] = useState(false);

  const { data } = candidateService.registrationReport.opadmin.useGetRegistrationReportExamLevel();
  const {
    form,
    submitGenerate,
    fileDownloadStatus,
    onDialogClose,
    dialogState,
    process,
  } = useRegistrationReport();
  const { handleSubmit, register, control, formState: { errors }, watch, trigger, setValue } = form;

  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');
  const watchSubjectExamLevel = watch('subjectExamLevel');
  const watchCandidateExamLevel = watch('candidateExamLevel');
  const watchSelectedExamLevel = watch('selectedExamLevel');
  const watchExamSeries = watch('examSeries');

  useEffect(() => {
    if (
      (watchStartDate && searchTouched) ||
      (watchEndDate && searchTouched) ||
      (watchSubjectExamLevel && searchTouched) ||
      (watchCandidateExamLevel && searchTouched) ||
      (watchSelectedExamLevel && searchTouched) ||
      (watchExamSeries && searchTouched)
    ) {
      trigger('endDate');
      trigger('custom');
      trigger('startDate');
      trigger('subjectExamLevel');
      trigger('candidateExamLevel');
      trigger('selectedExamLevel');
      trigger('examSeries');
    }
  }, [
    watchStartDate,
    trigger,
    searchTouched,
    watchEndDate,
    watchSubjectExamLevel,
    watchCandidateExamLevel,
    watchSelectedExamLevel,
    watchExamSeries,
  ]);

  useEffect(() => {
    if (!subjectExamValue.length) {
      setExamSeriesValue(null);
      setIsDisable(true);
      return;
    }
    setExamSeriesValue(null);
    const checkSubjectExamLevel = subjectExamValue.every((s) => !s.flag);
    setIsDisable(checkSubjectExamLevel);
  }, [subjectExamValue]);

  const checkStartDate = () => {
    if (startDate && endDate) {
      if (dayjs(endDate).diff(startDate, 'day') < 0) {
        form.setError('endDate', {
          type: 'custom',
          message: 'End Date should be greater than Start Date',
        });
        return false;
      }
      if (dayjs(endDate).diff(startDate, 'month', true) > parseFloat('3.0')) {
        form.setError('root.custom', {
          type: 'manual',
          message: 'Search range should not be more than 3 months.',
        });
        return false;
      }
    }
    return true;
  };

  const checkDateRangeHandler = (formValue: any) => {
    const checkDate = checkStartDate();
    if (!checkDate) {
      return;
    }
    const examSeriesFormValue = formValue?.examSeries
      ? new Array(formValue.examSeries)
      : data?.examSeries;
    submitGenerate({ ...formValue, examSeries: examSeriesFormValue });
  };

  const onSearchClickedHandler = async () => {
    if (Object.keys(errors).length === 0 && errors.constructor === Object) {
      setSearchTouched(true);
    } else {
      setSearchTouched(false);
    }
  };

  return (
    <ContentTemplate type="full">
      <SectionHeader title={t('admin->>RegistrationReportDetails.Report Details')} />
      <form noValidate onSubmit={handleSubmit(checkDateRangeHandler)}>
        <Box className={classes.formElementContainer}>
          <RegistrationSelectDate
            startDate={startDate}
            onSetStartDate={(date: any) => setStartDate(date)}
            endDate={endDate}
            onSetEndDate={(date: any) => setEndDate(date)}
            onSetValue={setValue}
            control={control}
            startDateErr={!!errors.startDate}
            startDateErrMsg={errors?.startDate?.message?.toString()}
            endDateErr={!!errors.endDate}
            endDateErrMsg={errors?.endDate?.message?.toString()}
            isError={!!errors?.root?.custom}
            cusErrorMsg={errors?.root?.custom?.message?.toString()}
          />
        </Box>

        <Box className={classes.formElementContainer}>
          <InputLabel required>{t('admin->>RegistrationReportDetails.ExamLevelType')}</InputLabel>
          <SingleSelectionRadio
            name="selectedExamLevel"
            data={[
              { label: t('admin->>RegistrationReportDetails.SearchBySubject'), value: 'subject' },
              {
                label: t('admin->>RegistrationReportDetails.SearchByCandidate'),
                value: 'candidate',
              },
            ]}
            onChange={(_event: any, value: string) => {
              setIsDisable(true);
              setSelectedValue(value);
              setExamSeriesValue(null);
              setValue('examSeries', null);
              setValue('selectedExamLevel', value);
              if(value === 'subject'){
                setValue('candidateExamLevel', []);
                form.unregister('candidateExamLevel')
              }else if(value === 'candidate'){
                setValue('subjectExamLevel', []);
                form.unregister('subjectExamLevel')
              }
            }}
            value={selectedValue}
            className={classes.radioContainer}
          />
          {errors?.selectedExamLevel?.message && (
            <ErrorInfo className={classes.errMsg} label={errors?.selectedExamLevel?.message?.toString()} />
          )}
          {selectedValue === 'subject' && (
            <RegistrationExamLevel
              isSubject
              label={t('admin->>RegistrationReportDetails.SubjectExamLevel')}
              data={data ? data?.subjectExamLevel : []}
              control={control}
              isError={!!errors?.subjectExamLevel}
              errorMsg={errors?.subjectExamLevel?.message?.toString()}
              valueTitle="subjectExamLevel"
              onSetExamValue={setSubjectExamValue}
              onSetValue={setValue}
              placeholder={watchSubjectExamLevel?.length ? '' : EXAM_LEVEL_PLACEHOLDER}
            />
          )}
          {selectedValue === 'candidate' && (
            <RegistrationExamLevel
              label={t('admin->>RegistrationReportDetails.CandidateExamLevel')}
              data={data ? data?.candidateExamLevel : []}
              control={control}
              isError={!!errors?.candidateExamLevel}
              errorMsg={errors?.candidateExamLevel?.message?.toString()}
              valueTitle="candidateExamLevel"
              onSetValue={setValue}
              placeholder={watchCandidateExamLevel?.length ? '' : EXAM_LEVEL_PLACEHOLDER}
              onSetIsDisable={setIsDisable}
            />
          )}
        </Box>
        {!isDisable && (
          <RegistrationExamSeries
            control={control}
            label={t('admin->>RegistrationReportDetails.ExamSeries')}
            data={data?.examSeries ? data?.examSeries : []}
            examSeriesValue={examSeriesValue}
            onSetExamSeries={setExamSeriesValue}
            onSetValue={setValue}
            isError={!!errors?.examSeries}
            errorMsg={errors?.examSeries?.message?.toString()}
          />
        )}
        <Box className={classes.submitButtonContainer}>
          <ApiButton status={process} type="submit" onClick={onSearchClickedHandler}>
            {t('admin->>RegistrationReportDetails.Generate Report')}
          </ApiButton>
        </Box>
      </form>
      <DownloadAuditProgressModal
        fileDownloadStatus={fileDownloadStatus}
        modalState={dialogState}
        onDialogClose={onDialogClose}
      />
    </ContentTemplate>
  );
};

export default RegistrationReportDetail;
